import type { ExpectedReturnsQuery, Page } from '@chukua/common';
import { Table } from 'antd';
import { SearchX, Truck } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { ExpectedReturnItem } from '../yard.api';
import { buildExpectedReturnColumns } from './build-expected-return-columns';

interface ExpectedReturnsTableProps {
  page: Page<ExpectedReturnItem>;
  list: ListQueryControls<ExpectedReturnsQuery>;
  isRefreshing: boolean;
}

/** Search box plus the server-paged table of trucks due back; the old rows stay up, dimmed, while the next page loads. The truck cell links to its receive screen. */
export function ExpectedReturnsTable({ page, list, isRefreshing }: ExpectedReturnsTableProps) {
  if (page.total === 0 && !list.isFiltered) {
    return (
      <EmptyState
        icon={Truck}
        title="No trucks due back"
        description="A truck appears here once its trip is delivered or its driver has done the post-trip check."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ListSearch value={list.query.q} placeholder="Search plate, driver or trip" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
      <Table
        rowKey="id"
        dataSource={page.items}
        columns={buildExpectedReturnColumns(list)}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={list.isFiltered ? { emptyText: <NoMatchingTrucks /> } : undefined}
        {...list.tableProps<ExpectedReturnItem>(page.total)}
      />
    </div>
  );
}

function NoMatchingTrucks() {
  return <EmptyState icon={SearchX} title="No matching trucks" description="No truck due back matches this search or these filters." />;
}
